import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiBell, FiCheck, FiGift, FiTag, FiCreditCard, FiAlertTriangle,
  FiCheckCircle, FiXCircle, FiInfo, FiAward,
} from 'react-icons/fi';
import notificationsService from '../services/notifications.service';
import type { Notification } from '../types';

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Notificações');
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await notificationsService.getNotifications();
      setNotifications(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lida: true } : n))
      );
    } catch (err) { console.error(err); }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, lida: true })));
    } catch (err) { console.error(err); }
  };

  const getIcon = (tipo?: string) => {
    switch ((tipo || '').toUpperCase()) {
      case 'PONTOS': return <FiAward />;
      case 'PROMOCAO': return <FiTag />;
      case 'TRANSACAO': return <FiCreditCard />;
      case 'ALERTA': case 'W': case 'WARNING': return <FiAlertTriangle />;
      case 'S': case 'SUCCESS': return <FiCheckCircle />;
      case 'E': case 'ERROR': return <FiXCircle />;
      case 'I': case 'INFO': return <FiInfo />;
      default: return <FiBell />;
    }
  };

  const getColor = (tipo?: string) => {
    switch ((tipo || '').toUpperCase()) {
      case 'PONTOS': return 'bg-amber-100 text-amber-700';
      case 'PROMOCAO': return 'bg-pink-100 text-pink-700';
      case 'TRANSACAO': return 'bg-green-100 text-green-700';
      case 'ALERTA': case 'W': case 'WARNING': return 'bg-orange-100 text-orange-700';
      case 'S': case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'E': case 'ERROR': return 'bg-red-100 text-red-700';
      default: return 'bg-purple-100 text-purple-700';
    }
  };

  const formatRelative = (iso: string) => {
    try {
      const dt = new Date(iso);
      const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
      if (diff < 60) return 'agora';
      if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `há ${Math.floor(diff / 86400)} dia${Math.floor(diff / 86400) > 1 ? 's' : ''}`;
      return dt.toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' });
    } catch { return iso; }
  };

  const unreadCount = notifications.filter((n) => !n.lida).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FiBell className="text-pagali-primary" />
            Notificações
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {notifications.length > 0
              ? `${notifications.length} notificaç${notifications.length > 1 ? 'ões' : 'ão'}`
              : 'Sem notificações'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="flex items-center gap-2 px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/90 transition-colors text-sm font-medium"
          >
            <FiCheck /> Marcar todas
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-pagali-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <FiGift className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Sem notificações</h3>
          <p className="text-sm text-gray-500">Aqui aparecerão as tuas notificações quando chegarem.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => !n.lida && handleMarkAsRead(n.id)}
              className={`bg-white rounded-xl p-4 border transition-all cursor-pointer ${
                n.lida ? 'border-gray-100' : 'border-pagali-primary/30 shadow-sm'
              } hover:shadow-md`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl ${getColor(n.tipo)}`}>
                  {getIcon(n.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`text-sm ${n.lida ? 'font-medium text-gray-700' : 'font-bold text-gray-900'}`}>
                      {n.titulo}
                    </h3>
                    {!n.lida && (
                      <div className="w-2 h-2 bg-pagali-primary rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{n.mensagem}</p>
                  <p className="text-xs text-gray-400 mt-2">{formatRelative(n.data)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
